import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react'
import { useHover } from '../Common/Hover'
import { useCanvasGestures } from '../../hooks/useCanvasGestures'
import { usePersistedState } from '../../hooks/usePersistedState'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import { DEFAULT_VIEWPORT_HEIGHT } from './boardItem'
import { buildChecklistNodeContent, buildChecklistWireContent } from './hoverContent'
import { checklistBoardSessionSnapshotCache, cloneBoardSnapshot } from './sessionSnapshot'
import type { DragState } from './canvasMisc'

export const BOARD_SCALE_MIN = 0.5
export const BOARD_SCALE_MAX = 1.6
export const BOARD_SCALE_STEP = 0.1

export function useBoardViewport({
  timelineChecklistItemsRef,
  timelineChecklistItems,
  isBoardDirty,
  hasBoardSnapshotRef,
  nodes,
  boardSessionSnapshotKey,
  boardRef,
  isCompact,
}: {
  timelineChecklistItemsRef: MutableRefObject<BoardChecklistItem[]>
  timelineChecklistItems: BoardChecklistItem[]
  isBoardDirty: boolean
  hasBoardSnapshotRef: MutableRefObject<boolean>
  nodes: BoardChecklistItem[]
  boardSessionSnapshotKey: string
  boardRef: MutableRefObject<HTMLDivElement | null>
  isCompact: boolean
}) {
  // Uniform canvas-world zoom. The viewport frame stays the same physical
  // size; only the inner world (bubbles, gates, SVG wires) scales together
  // via CSS `transform: scale()`. The outer container's `width/height` are
  // multiplied by `boardScale` so scrollbars track the actual scaled content
  // — and with content<viewport the boardMetrics.width clamp to viewportWidth
  // means no scrollbars appear when the canvas is empty at the ends.
  const [boardScale, setBoardScale] = usePersistedState<number>('drawingBoard.boardScale', 0.6)

  useEffect(() => {
    timelineChecklistItemsRef.current = timelineChecklistItems
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: timelineChecklistItemsRef are useRef/useState identities passed from the Surface (stable)
  }, [timelineChecklistItems])

  const prevSnapshotKeyRef = useRef(boardSessionSnapshotKey)
  useEffect(() => {
    if (prevSnapshotKeyRef.current !== boardSessionSnapshotKey) {
      prevSnapshotKeyRef.current = boardSessionSnapshotKey
      return
    }
    if (isBoardDirty) return
    if (!hasBoardSnapshotRef.current && nodes.length === 0) return
    checklistBoardSessionSnapshotCache.set(boardSessionSnapshotKey, cloneBoardSnapshot(nodes))
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: hasBoardSnapshotRef are useRef/useState identities passed from the Surface (stable)
  }, [boardSessionSnapshotKey, isBoardDirty, nodes])
  const adjustBoardScale = (direction: 1 | -1) => {
    setBoardScale((current) => {
      const next = Math.round((current + direction * BOARD_SCALE_STEP) * 10) / 10
      return Math.max(BOARD_SCALE_MIN, Math.min(BOARD_SCALE_MAX, next))
    })
  }

  // Touch gestures: two-finger pinch updates boardScale; double-tap toggles
  // between the 0.6 default and 1x. Single-finger pan is owned by
  // the native scroll container (boardRef has `touch-action: pan-x pan-y`).
  useCanvasGestures(
    boardRef,
    {
      onPinch: (_x, _y, scaleFactor) => {
        setBoardScale((current) => {
          const adjustedFactor = isCompact ? 1 + ((scaleFactor - 1) * 0.72) : scaleFactor
          const next = current * adjustedFactor
          return Math.max(BOARD_SCALE_MIN, Math.min(BOARD_SCALE_MAX, next))
        })
      },
      onDoubleTap: () => {
        setBoardScale((current) => (current > 0.65 ? (isCompact ? 0.55 : 0.6) : 1))
      },
    },
    { enableSingleFingerPan: false, momentumDecay: isCompact ? 0.9 : 0.92, maxMomentumFrames: isCompact ? 90 : 120 },
  )
  const [isPlacingNode, setIsPlacingNode] = useState(false)
  const [wireDraft, setWireDraft] = useState<Extract<DragState, { type: 'wire' }> | null>(null)
  const [flashMessage, setFlashMessage] = useState<string | null>(null)
  const [breakingWires, setBreakingWires] = useState<Array<{ id: string; path: string }>>([])
  const [viewportSize, setViewportSize] = useState({
    width: 0,
    height: DEFAULT_VIEWPORT_HEIGHT,
    scrollLeft: 0,
    scrollTop: 0,
  })
  const checklistHover = useHover({ variant: 'rich', placement: 'top', delay: { open: 220, close: 0 }, group: 'checklistCanvas' })
  const { show: showChecklistHoverRaw, hide: hideChecklistHoverRaw } = checklistHover
  const checklistHoverAnchorRef = useRef<Element | null>(null)
  const collectChecklistAvoidZones = useCallback(() => {
    if (typeof document === 'undefined') return []
    const anchor = checklistHoverAnchorRef.current
    const nodes = document.querySelectorAll<HTMLElement>('[data-board-bubble-id]')
    const rects: DOMRect[] = []
    nodes.forEach((node) => {
      if (anchor && node === anchor) return
      const rect = node.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) return
      rects.push(rect)
    })
    return rects
  }, [])
  const showChecklistNodeHover = useCallback((anchor: Element, node: BoardChecklistItem, parentPath: string, parentColor: string) => {
    checklistHoverAnchorRef.current = anchor
    showChecklistHoverRaw({ anchor, content: buildChecklistNodeContent(node, parentPath, parentColor) })
  }, [showChecklistHoverRaw])
  const showChecklistWireHover = useCallback((anchor: Element, source: BoardChecklistItem, target: BoardChecklistItem) => {
    checklistHoverAnchorRef.current = anchor
    showChecklistHoverRaw({ anchor, content: buildChecklistWireContent(source, target) })
  }, [showChecklistHoverRaw])
  const hideChecklistHover = useCallback(() => {
    checklistHoverAnchorRef.current = null
    hideChecklistHoverRaw()
  }, [hideChecklistHoverRaw])
  const [externalWorkspaceProgressByToken, setExternalWorkspaceProgressByToken] = useState<Map<string, number>>(new Map())
  const [isBoardToolMenuOpen, setIsBoardToolMenuOpen] = usePersistedState<boolean>('drawingBoard.isBoardToolMenuOpen', false)
  const [isMiniMapVisible, setIsMiniMapVisible] = usePersistedState<boolean>('drawingBoard.isMiniMapVisible', false)
  const [isMarkdownPanelOpen] = usePersistedState<boolean>('drawingBoard.isMarkdownPanelOpen', false)
  const [isClipboardWatcherEnabled] = useState(false)
  const [, setTimerTick] = useState(0)

  return {
    boardScale,
    setBoardScale,
    BOARD_SCALE_MIN,
    BOARD_SCALE_MAX,
    BOARD_SCALE_STEP,
    adjustBoardScale,
    isPlacingNode,
    setIsPlacingNode,
    wireDraft,
    setWireDraft,
    flashMessage,
    setFlashMessage,
    breakingWires,
    setBreakingWires,
    viewportSize,
    setViewportSize,
    checklistHover,
    checklistHoverAnchorRef,
    collectChecklistAvoidZones,
    showChecklistNodeHover,
    showChecklistWireHover,
    hideChecklistHover,
    externalWorkspaceProgressByToken,
    setExternalWorkspaceProgressByToken,
    isBoardToolMenuOpen,
    setIsBoardToolMenuOpen,
    isMiniMapVisible,
    setIsMiniMapVisible,
    isMarkdownPanelOpen,
    isClipboardWatcherEnabled,
    setTimerTick,
  }
}
